// The plugin host's state, without React: what each plugin registered
// (contributions to slots, pages, named APIs, event listeners) and the
// notifications on screen. Piral kept the same in its global state; the
// React side (slot, routes, notifications) reads it through `subscribe`.
// Every collection is replaced, never mutated, so a reader can compare
// snapshots by reference.

export interface Contribution {
  /** What the slot renders: the registered component wrapped by the host. */
  component: unknown;
  /** What the plugin registered; `unregisterExtension` finds it by this. */
  reference: unknown;
  defaults: any;
  pilet: string;
}

export interface Page {
  route: string;
  component: unknown;
  meta: Record<string, unknown>;
  pilet: string;
}

export interface Notification {
  id: number;
  content: unknown;
  options: { title?: string; autoClose?: number; type?: 'info' | 'success' | 'warning' | 'error' };
  pilet: string;
}

type Callback = (arg: any) => void;

const NONE: readonly never[] = Object.freeze([]);

export class Registry {
  private slots = new Map<string, readonly Contribution[]>();
  private pageList: readonly Page[] = NONE;
  private notificationList: readonly Notification[] = NONE;
  private readonly apis = new Map<string, unknown>();
  private readonly providers = new Map<string, string>();
  private readonly waiting = new Map<string, Array<(impl: unknown) => void>>();
  private readonly listeners = new Map<string, Array<{ callback: Callback; pilet: string }>>();
  private readonly watchers = new Set<() => void>();
  private nextNotification = 1;
  private readonly timers = new Map<number, ReturnType<typeof setTimeout>>();

  private readonly report: (message: string, error: unknown) => void;

  /** `seed`: APIs the platform itself provides, registered before any plugin. `report`: where failures go. */
  constructor(
    seed: Record<string, unknown> = {},
    report: (message: string, error: unknown) => void = (message, error) => console.error(message, error),
  ) {
    this.report = report;
    for (const [name, impl] of Object.entries(seed)) {
      this.apis.set(name, impl);
      this.providers.set(name, 'platform');
    }
  }

  // ── change notification ────────────────────────────────────────────────

  /** Calls `watcher` after every change to slots, pages or notifications. */
  subscribe(watcher: () => void): () => void {
    this.watchers.add(watcher);
    return () => this.watchers.delete(watcher);
  }

  private changed(): void {
    for (const w of [...this.watchers]) w();
  }

  // ── slots ──────────────────────────────────────────────────────────────

  /** The contributions to a slot in registration order; the same array until the slot changes. */
  extensions(slot: string): readonly Contribution[] {
    return this.slots.get(slot) ?? NONE;
  }

  registerExtension(slot: string, contribution: Contribution): () => void {
    this.slots.set(slot, [...this.extensions(slot), contribution]);
    this.changed();
    return () => this.unregisterExtension(slot, contribution.reference);
  }

  /** Removes every contribution to `slot` that registered `reference`, as Piral did. */
  unregisterExtension(slot: string, reference: unknown): void {
    const before = this.extensions(slot);
    const after = before.filter((c) => c.reference !== reference);
    if (after.length === before.length) return;
    this.slots.set(slot, after.length ? after : NONE);
    this.changed();
  }

  // ── pages ──────────────────────────────────────────────────────────────

  /** Every page in registration order; the same array until one changes. */
  pages(): readonly Page[] {
    return this.pageList;
  }

  /** A route has one page; registering it again replaces it (a plugin set up again). */
  registerPage(page: Page): () => void {
    this.pageList = [...this.pageList.filter((p) => p.route !== page.route), page];
    this.changed();
    return () => this.unregisterPage(page.route);
  }

  unregisterPage(route: string): void {
    const after = this.pageList.filter((p) => p.route !== route);
    if (after.length === this.pageList.length) return;
    this.pageList = after;
    this.changed();
  }

  // ── named APIs ─────────────────────────────────────────────────────────

  /** One provider per name; the same plugin registering again replaces its implementation. */
  registerApi(pilet: string, name: string, implementation: unknown): void {
    const provider = this.providers.get(name);
    if (provider !== undefined && provider !== pilet) {
      throw new Error(`API "${name}" is already provided by ${provider}; ${pilet} cannot provide it too.`);
    }
    this.providers.set(name, pilet);
    this.apis.set(name, implementation);
    for (const resolve of this.waiting.get(name) ?? []) resolve(implementation);
    this.waiting.delete(name);
  }

  /** Resolves as soon as the API is provided, whatever order the plugins set up in. */
  getApi<T>(name: string): Promise<T> {
    const found = this.apis.get(name);
    if (found !== undefined) return Promise.resolve(found as T);
    return new Promise<T>((resolve) => {
      this.waiting.set(name, [...(this.waiting.get(name) ?? []), resolve as (impl: unknown) => void]);
    });
  }

  tryGetApi<T>(name: string): T | undefined {
    return this.apis.get(name) as T | undefined;
  }

  // ── events ─────────────────────────────────────────────────────────────

  on(pilet: string, type: string, callback: Callback): void {
    this.listeners.set(type, [...(this.listeners.get(type) ?? []), { callback, pilet }]);
  }

  /** Removes the first listener registered with `callback`, as Piral did. */
  off(type: string, callback: Callback): void {
    const list = this.listeners.get(type) ?? [];
    const i = list.findIndex((l) => l.callback === callback);
    if (i < 0) return;
    this.listeners.set(type, [...list.slice(0, i), ...list.slice(i + 1)]);
  }

  /**
   * Calls every listener now, in the order they were added. A listener that
   * throws is reported and the others still run, as with the DOM events
   * Piral dispatched.
   */
  emit(type: string, arg: unknown): void {
    for (const { callback } of this.listeners.get(type) ?? []) {
      try {
        callback(arg);
      } catch (error) {
        this.report(`a listener of "${type}" failed`, error);
      }
    }
  }

  // ── notifications ──────────────────────────────────────────────────────

  notifications(): readonly Notification[] {
    return this.notificationList;
  }

  /** Shows a notification; it closes after `autoClose` ms if given, or when the returned function is called. */
  showNotification(pilet: string, content: unknown, options: Notification['options'] = {}): () => void {
    const id = this.nextNotification++;
    this.notificationList = [...this.notificationList, { id, content, options, pilet }];
    this.changed();
    const close = () => this.closeNotification(id);
    if (options.autoClose && options.autoClose > 0) this.timers.set(id, setTimeout(close, options.autoClose));
    return close;
  }

  closeNotification(id: number): void {
    const timer = this.timers.get(id);
    if (timer !== undefined) clearTimeout(timer);
    this.timers.delete(id);
    const after = this.notificationList.filter((n) => n.id !== id);
    if (after.length === this.notificationList.length) return;
    this.notificationList = after;
    this.changed();
  }

  // ── a plugin as a whole ────────────────────────────────────────────────

  /**
   * Removes everything a plugin registered: its contributions, pages,
   * listeners and notifications, so that setting it up again (a hot update)
   * does not register twice. Its APIs stay: their consumers hold the old
   * implementation, and the plugin registering again replaces it.
   */
  disposePlugin(pilet: string): void {
    for (const [slot, list] of this.slots) {
      const after = list.filter((c) => c.pilet !== pilet);
      if (after.length !== list.length) this.slots.set(slot, after.length ? after : NONE);
    }
    this.pageList = this.pageList.filter((p) => p.pilet !== pilet);
    for (const [type, list] of this.listeners) this.listeners.set(type, list.filter((l) => l.pilet !== pilet));
    for (const n of this.notificationList) if (n.pilet === pilet) this.closeNotification(n.id);
    this.changed();
  }
}
